import { useState, useEffect } from 'react';
import moment from 'moment-timezone';

/**
 * Formats a time string from HH:mm to readable format (e.g., "3 pm" or "3:30 pm")
 */
const formatDaypartTime = time => {
  if (!time) return '';
  const [hour, minute] = time.split(':').map(Number);
  const period = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour > 12 ? hour - 12 : hour || 12;
  return minute === 0 ? `${displayHour} ${period}` : `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

/**
 * Hook to calculate the remaining time until a daypart ends
 * @param {Object} daypart - Daypart object with startTime and endTime (HH:mm format)
 * @param {string} timezone - IANA timezone string (e.g., "Australia/Sydney")
 * @returns {Object} { minutesRemaining, secondsRemaining, isStarting, isActive }
 */
const useDaypartCountdown = (daypart, timezone) => {
  const [minutesRemaining, setMinutesRemaining] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!daypart?.startTime || !daypart?.endTime) {
      setMinutesRemaining(null);
      setSecondsRemaining(null);
      setIsStarting(false);
      setIsActive(false);
      return undefined;
    }

    const calculateTimeRemaining = () => {
      const now = timezone ? moment().tz(timezone) : moment();
      const [startHour, startMinute] = daypart.startTime.split(':').map(Number);
      const [endHour, endMinute] = daypart.endTime.split(':').map(Number);

      let startTime = now
        .clone()
        .hour(startHour)
        .minute(startMinute)
        .second(0)
        .millisecond(0);

      let endTime = now
        .clone()
        .hour(endHour)
        .minute(endMinute)
        .second(0)
        .millisecond(0);

      // Handle overnight ranges
      const isOvernightRange = startHour > endHour;
      if (isOvernightRange) {
        if (now.hour() < endHour) {
          startTime = startTime.subtract(1, 'day');
        } else {
          endTime = endTime.add(1, 'day');
        }
      }

      // Check if we're before the start time
      if (now.isBefore(startTime)) {
        const minutesUntilStart = startTime.diff(now, 'minutes');
        const totalSecondsUntilStart = startTime.diff(now, 'seconds');
        if (minutesUntilStart <= 30) {
          setIsStarting(true);
          setIsActive(false);
          setSecondsRemaining(totalSecondsUntilStart);
          return minutesUntilStart;
        }
        setIsStarting(false);
        setIsActive(false);
        setSecondsRemaining(null);
        return null;
      }

      setIsStarting(false);

      // Check if we're past the end time
      if (now.isAfter(endTime)) {
        setIsActive(false);
        setSecondsRemaining(null);
        return null;
      }

      // We're within the daypart window
      setIsActive(true);
      const minutesUntilEnd = endTime.diff(now, 'minutes');
      const totalSecondsUntilEnd = endTime.diff(now, 'seconds');
      setSecondsRemaining(totalSecondsUntilEnd > 0 ? totalSecondsUntilEnd : null);
      return minutesUntilEnd > 0 ? minutesUntilEnd : null;
    };

    const initialRemaining = calculateTimeRemaining();
    setMinutesRemaining(initialRemaining);

    // Tick every second for precise countdown when nearing end
    const interval = setInterval(() => {
      const updatedRemaining = calculateTimeRemaining();
      setMinutesRemaining(updatedRemaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [daypart, timezone]);

  return { minutesRemaining, secondsRemaining, isStarting, isActive };
};

export { useDaypartCountdown, formatDaypartTime };
